import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ThumbsUp, Send, User, Calendar, Trash2 } from 'lucide-react';
import FeedbackStatusBadge, { statusConfig } from './FeedbackStatusBadge';

const FeedbackDetail = ({ feedbackId, onBack, onRefresh }) => {
    const [feedback, setFeedback] = useState(null);
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(0);
    const [error, setError] = useState(null);

    const fetchFeedbackDetail = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`index.php/feedbacks/getDetail/${feedbackId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch feedback details');
            }
            const data = await response.json();
            setFeedback(data.feedback);
            setComments(data.comments);
            setIsAdmin(data.is_admin);
            setSelectedStatus(data.feedback.status);
        } catch (err) {
            console.error('Error fetching feedback detail:', err);
            setError('Fehler beim Laden des Feedbacks');
        } finally {
            setIsLoading(false);
        }
    }, [feedbackId]);

    useEffect(() => {
        if (feedbackId) {
            fetchFeedbackDetail();
        }
    }, [feedbackId, fetchFeedbackDetail]);

    const handleLike = async () => {
        if (!feedback || feedback.user_has_liked) return;

        try {
            const response = await fetch('index.php/feedbacks/toggleLike', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedbackId: feedback.feedback_id }),
            });

            const result = await response.json();
            if (response.ok && result.success) {
                setFeedback((prev) => ({
                    ...prev,
                    likes: result.likes,
                    user_has_liked: true,
                }));
                onRefresh?.();
            }
        } catch (err) {
            console.error('Error liking feedback:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || isSubmittingComment) return;

        setIsSubmittingComment(true);
        try {
            const response = await fetch('index.php/feedbacks/addComment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedbackId: feedback.feedback_id,
                    comment: newComment.trim(),
                }),
            });

            const result = await response.json();
            if (response.ok && result.success) {
                setComments(result.comments);
                setNewComment('');
                setFeedback((prev) => ({
                    ...prev,
                    comment_count: result.comment_count,
                }));
                onRefresh?.();
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleStatusChange = async () => {
        if (!isAdmin || isUpdatingStatus) return;

        setIsUpdatingStatus(true);
        try {
            const response = await fetch('index.php/feedbacks/updateStatus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    feedbackId: feedback.feedback_id,
                    status: parseInt(selectedStatus, 10),
                }),
            });

            const result = await response.json();
            if (response.ok && result.success) {
                setFeedback((prev) => ({
                    ...prev,
                    status: result.status,
                    status_name: result.status_name,
                }));
                onRefresh?.();
            }
        } catch (err) {
            console.error('Error updating status:', err);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDelete = async () => {
        if (!isAdmin || isDeleting) return;

        setIsDeleting(true);
        try {
            const response = await fetch('index.php/feedbacks/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedbackId: feedback.feedback_id }),
            });

            const result = await response.json();
            if (response.ok && result.success) {
                onRefresh?.();
                onBack?.();
            }
        } catch (err) {
            console.error('Error deleting feedback:', err);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Laden...</span>
                </div>
            </div>
        );
    }

    if (error || !feedback) {
        return (
            <div className="text-center py-5">
                <p className="text-danger">{error || 'Feedback nicht gefunden'}</p>
                <button onClick={onBack} className="btn btn-outline-secondary mt-3">
                    <ArrowLeft size={18} className="me-2" />
                    Zurück zur Liste
                </button>
            </div>
        );
    }

    return (
        <div>
            {/* Back Link */}
            <button
                onClick={onBack}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary, #64748b)',
                    marginBottom: '0.75rem',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-slate, #5b7c8d)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary, #64748b)')}
            >
                <ArrowLeft size={16} />
                Zurück zur Liste
            </button>

            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1.5rem',
                    gap: '1rem',
                    flexWrap: 'wrap',
                }}
            >
                <div>
                    <h1
                        className="h3"
                        style={{
                            fontWeight: 600,
                            color: 'var(--text-primary, #2c3e50)',
                            marginBottom: '0.5rem',
                        }}
                    >
                        {feedback.titel}
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <FeedbackStatusBadge status={feedback.status} size="default" />
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.875rem',
                            }}
                        >
                            <User size={14} />
                            {feedback.user_shortname}
                        </span>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.875rem',
                            }}
                        >
                            <Calendar size={14} />
                            {feedback.datum_formatted}
                        </span>
                    </div>
                </div>

                {/* Like Button */}
                <button
                    onClick={handleLike}
                    disabled={feedback.user_has_liked || feedback.status === 3 || feedback.status === 4}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        border: `1px solid ${feedback.user_has_liked || feedback.status === 3 || feedback.status === 4 ? 'var(--border-light, #e2e8f0)' : 'var(--accent-slate, #5b7c8d)'}`,
                        borderRadius: '8px',
                        background: feedback.user_has_liked || feedback.status === 3 || feedback.status === 4 ? 'var(--bg-secondary, #f5f7f9)' : 'transparent',
                        color: feedback.user_has_liked || feedback.status === 3 || feedback.status === 4 ? 'var(--text-muted, #94a3b8)' : 'var(--accent-slate, #5b7c8d)',
                        cursor: feedback.user_has_liked || feedback.status === 3 || feedback.status === 4 ? 'not-allowed' : 'pointer',
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                    }}
                    title={
                        feedback.status === 3
                            ? 'Erledigte Feedbacks können nicht mehr geliked werden'
                            : feedback.status === 4
                            ? 'Abgelehnte Feedbacks können nicht mehr geliked werden'
                            : feedback.user_has_liked
                            ? 'Bereits abgestimmt'
                            : 'Like'
                    }
                >
                    <ThumbsUp size={18} />
                    {feedback.likes} {feedback.likes === 1 ? 'Like' : 'Likes'}
                </button>
            </div>

            {/* Description Card */}
            <div
                style={{
                    backgroundColor: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                }}
            >
                <div
                    style={{
                        color: 'var(--text-primary, #2c3e50)',
                        fontSize: '0.9375rem',
                        lineHeight: 1.7,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {feedback.beschreibung}
                </div>
            </div>

            {/* Admin Status Change */}
            {isAdmin && (
                <div
                    style={{
                        backgroundColor: 'var(--bg-card, #ffffff)',
                        border: '1px solid var(--border-light, #e2e8f0)',
                        borderRadius: '12px',
                        padding: '1.25rem 1.5rem',
                        marginBottom: '1.5rem',
                    }}
                >
                    <h5
                        style={{
                            margin: 0,
                            marginBottom: '1rem',
                            fontWeight: 600,
                            color: 'var(--text-primary, #2c3e50)',
                            fontSize: '0.9375rem',
                        }}
                    >
                        Status anpassen
                    </h5>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                style={{
                                    padding: '0.5rem 2rem 0.5rem 0.75rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    backgroundColor: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-primary, #2c3e50)',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value={0}>Eingereicht</option>
                                <option value={1}>Wird berücksichtigt</option>
                                <option value={2}>Wird umgesetzt</option>
                                <option value={3}>Erledigt</option>
                                <option value={4}>Abgelehnt</option>
                            </select>
                            <button
                                onClick={handleStatusChange}
                                disabled={isUpdatingStatus || parseInt(selectedStatus, 10) === feedback.status}
                                style={{
                                    padding: '0.5rem 1rem',
                                    border: 'none',
                                    borderRadius: '8px',
                                    backgroundColor: 'var(--accent-slate, #5b7c8d)',
                                    color: '#ffffff',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: isUpdatingStatus || parseInt(selectedStatus, 10) === feedback.status ? 'not-allowed' : 'pointer',
                                    opacity: isUpdatingStatus || parseInt(selectedStatus, 10) === feedback.status ? 0.6 : 1,
                                }}
                            >
                                {isUpdatingStatus ? 'Wird gespeichert...' : 'Status ändern'}
                            </button>
                        </div>

                        {!showDeleteConfirm ? (
                            <button
                                onClick={() => setShowDeleteConfirm(true)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '0.5rem 1rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '8px',
                                    backgroundColor: 'transparent',
                                    color: '#dc3545',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                }}
                            >
                                <Trash2 size={16} />
                                Löschen
                            </button>
                        ) : (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.875rem', color: '#dc3545', fontWeight: 500 }}>
                                    Wirklich löschen?
                                </span>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: 'none',
                                        borderRadius: '8px',
                                        backgroundColor: '#dc3545',
                                        color: '#ffffff',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                                        opacity: isDeleting ? 0.6 : 1,
                                    }}
                                >
                                    {isDeleting ? 'Wird gelöscht...' : 'Ja, löschen'}
                                </button>
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: '1px solid var(--border-medium, #cbd5e1)',
                                        borderRadius: '8px',
                                        backgroundColor: 'transparent',
                                        color: 'var(--text-secondary, #64748b)',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Abbrechen
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Comments Section */}
            <div
                style={{
                    backgroundColor: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid var(--border-light, #e2e8f0)',
                        backgroundColor: 'var(--bg-secondary, #f5f7f9)',
                    }}
                >
                    <h5
                        style={{
                            margin: 0,
                            fontWeight: 600,
                            color: 'var(--text-primary, #2c3e50)',
                            fontSize: '0.9375rem',
                        }}
                    >
                        Kommentare ({comments.length})
                    </h5>
                </div>

                {/* Comments List */}
                {comments.length > 0 ? (
                    <div style={{ padding: '1rem 1.5rem' }}>
                        {comments.map((comment, index) => (
                            <div
                                key={comment.kommentar_id}
                                style={{
                                    padding: '1rem 0',
                                    borderBottom: index < comments.length - 1 ? '1px solid var(--border-light, #e2e8f0)' : 'none',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 500,
                                            color: 'var(--text-primary, #2c3e50)',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {comment.user_fullname}
                                    </span>
                                    <span
                                        style={{
                                            color: 'var(--text-muted, #94a3b8)',
                                            fontSize: '0.8125rem',
                                        }}
                                    >
                                        {comment.datum_formatted}
                                    </span>
                                </div>
                                <p
                                    style={{
                                        margin: 0,
                                        color: 'var(--text-secondary, #64748b)',
                                        fontSize: '0.875rem',
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {comment.kommentar}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div
                        style={{
                            padding: '2rem 1.5rem',
                            textAlign: 'center',
                            color: 'var(--text-muted, #94a3b8)',
                        }}
                    >
                        Noch keine Kommentare vorhanden.
                    </div>
                )}

                {/* Add Comment Form */}
                <form
                    onSubmit={handleAddComment}
                    style={{
                        padding: '1rem 1.5rem',
                        borderTop: '1px solid var(--border-light, #e2e8f0)',
                        backgroundColor: 'var(--bg-secondary, #f5f7f9)',
                    }}
                >
                    <div style={{ marginBottom: '0.75rem' }}>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 500,
                                color: 'var(--text-primary, #2c3e50)',
                                fontSize: '0.875rem',
                            }}
                        >
                            Kommentar schreiben
                        </label>
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Schreiben Sie Ihren Kommentar..."
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '1px solid var(--border-medium, #cbd5e1)',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                backgroundColor: 'var(--bg-card, #ffffff)',
                                color: 'var(--text-primary, #2c3e50)',
                                outline: 'none',
                                resize: 'vertical',
                                minHeight: '100px',
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="submit"
                            disabled={!newComment.trim() || isSubmittingComment}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '0.5rem 1rem',
                                border: 'none',
                                borderRadius: '8px',
                                backgroundColor: 'var(--accent-slate, #5b7c8d)',
                                color: '#ffffff',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                cursor: !newComment.trim() || isSubmittingComment ? 'not-allowed' : 'pointer',
                                opacity: !newComment.trim() || isSubmittingComment ? 0.6 : 1,
                            }}
                        >
                            <Send size={16} />
                            {isSubmittingComment ? 'Wird gesendet...' : 'Kommentar senden'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FeedbackDetail;
